import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { normalizePartyEntityKind, type PartyEntityKind } from "@/lib/parties/entityKind";
import { normalizePartyType } from "@/lib/parties/partyTypes";
import { isAxiosError } from "axios";

export type CustomerPartyType = "all" | "customer";

export interface CustomerRecord {
  id: string;
  name: string;
  fatherName: string;
  cnic: string;
  ntn: string;
  contactPerson: string;
  country: number | null;
  countryName: string;
  state: number | null;
  stateName: string;
  city: string;
  postalCode: string;
  addressLine1: string;
  addressLine2: string;
  /** Combined address_line_1 + address_line_2 for search/display convenience. */
  address: string;
  phone: string;
  email: string;
  type: CustomerPartyType;
  entityKind: PartyEntityKind;
  totalPurchases: number;
  totalSpent: number;
  /** Absolute party-ledger closing balance (receivable/payable accounts). */
  balance: number;
  balanceSide: "Dr" | "Cr";
  currency: string;
  createdAt: string;
  updatedAt: string;
}

export type CustomerDraft = Pick<
  CustomerRecord,
  "name" | "fatherName" | "cnic" | "ntn" | "contactPerson" | "phone" | "email" | "city" | "type" | "entityKind"
> & {
  country: number | null;
  state: number | null;
  postalCode: string;
  addressLine1: string;
  addressLine2: string;
};

const LS_KEY = "inventory.customers.v3";

type CustomerApiRecord = {
  id?: number | string;
  name: string;
  father_name?: string | null;
  cnic?: string | null;
  ntn?: string | null;
  contact_person?: string | null;
  country?: number | string | null;
  country_name?: string | null;
  state?: number | string | null;
  state_name?: string | null;
  city?: string | null;
  postal_code?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  type?: string | null;
  entity_kind?: string | null;
  total_purchases?: number | string | null;
  total_spent?: number | string | null;
  balance?: number | string | null;
  balance_side?: string | null;
  currency?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertCustomerApiResponse = {
  message?: string;
  data?: CustomerApiRecord;
};

type DeleteCustomerApiResponse = {
  message?: string;
};

type UpsertCustomerResult = {
  record: CustomerRecord;
  message: string;
};

type DeleteCustomerResult = {
  success: boolean;
  message: string;
};

export const emptyCustomerDraft = (): CustomerDraft => ({
  name: "",
  fatherName: "",
  cnic: "",
  ntn: "",
  contactPerson: "",
  phone: "",
  email: "",
  type: "customer",
  entityKind: "individual",
  country: null,
  state: null,
  city: "",
  postalCode: "",
  addressLine1: "",
  addressLine2: "",
});

export function formatCustomerDisplayName(
  customer: Pick<CustomerRecord, "name" | "fatherName" | "entityKind">,
): string {
  const name = customer.name.trim();
  if (normalizePartyEntityKind(customer.entityKind) === "business") {
    return name || "—";
  }
  const father = customer.fatherName.trim();
  if (!name) return father || "—";
  if (!father) return name;
  return `${name} (S/O ${father})`;
}

export function customerErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Something went wrong. Please try again.";
}

function num(raw: number | string | null | undefined, fallback = 0): number {
  if (raw == null || raw === "") return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

function dedupeById(items: CustomerRecord[]): CustomerRecord[] {
  const byId = new Map<ID, CustomerRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function extractRowsFromResponse(body: unknown): CustomerApiRecord[] {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;

  const fromBlock = (block: unknown): CustomerApiRecord[] | null => {
    if (Array.isArray(block)) return block as CustomerApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as CustomerApiRecord[];
    }
    return null;
  };

  for (const key of ["data", "customers", "items"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function mapApiRecord(record: CustomerApiRecord): CustomerRecord {
  const created = record.created_at || new Date().toISOString();
  const line1 = record.address_line_1 ?? record.address ?? "";
  const line2 = record.address_line_2 ?? "";
  const combined = [line1, line2].map((s) => s.trim()).filter(Boolean).join(", ");
  const balance = Math.abs(Number(record.balance ?? 0));
  return {
    id: String(record.id ?? ""),
    name: record.name,
    fatherName: record.father_name ?? "",
    cnic: record.cnic ?? "",
    ntn: record.ntn ?? "",
    contactPerson: record.contact_person ?? "",
    country: record.country != null && record.country !== "" ? Number(record.country) : null,
    countryName: record.country_name ?? "",
    state: record.state != null && record.state !== "" ? Number(record.state) : null,
    stateName: record.state_name ?? "",
    city: record.city ?? "",
    postalCode: record.postal_code ?? "",
    addressLine1: line1,
    addressLine2: line2,
    address: combined,
    phone: record.phone ?? "",
    email: record.email ?? "",
    type: normalizePartyType(record.type, "customer") as CustomerPartyType,
    entityKind: normalizePartyEntityKind(record.entity_kind),
    totalPurchases: Math.round(num(record.total_purchases, 0)),
    totalSpent: num(record.total_spent, 0),
    balance: Number.isFinite(balance) ? Math.round(balance * 100) / 100 : 0,
    balanceSide: record.balance_side === "Cr" ? "Cr" : "Dr",
    currency: (record.currency ?? "PKR").toString().trim() || "PKR",
    createdAt: created,
    updatedAt: record.updated_at ?? created,
  };
}

export function normalizeCustomerDraft(draft: CustomerDraft): CustomerDraft {
  return {
    name: draft.name.trim(),
    fatherName: draft.fatherName?.trim() ?? "",
    cnic: draft.cnic?.trim() ?? "",
    ntn: draft.ntn?.trim() ?? "",
    contactPerson: draft.contactPerson?.trim() ?? "",
    phone: draft.phone?.trim() ?? "",
    email: draft.email?.trim() ?? "",
    type: normalizePartyType(draft.type, "customer") as CustomerPartyType,
    entityKind: normalizePartyEntityKind(draft.entityKind),
    country: draft.country ?? null,
    state: draft.state ?? null,
    city: draft.city?.trim() ?? "",
    postalCode: draft.postalCode?.trim() ?? "",
    addressLine1: draft.addressLine1?.trim() ?? "",
    addressLine2: draft.addressLine2?.trim() ?? "",
  };
}

export type CustomerDraftValidation = {
  valid: boolean;
  message?: string;
};

export function validateCustomerDraft(draft: CustomerDraft): CustomerDraftValidation {
  const n = normalizeCustomerDraft(draft);
  if (!n.name) {
    return {
      valid: false,
      message: n.entityKind === "business" ? "Business name is required." : "Customer name is required.",
    };
  }
  // Father name and CNIC are optional
  if (!n.phone) return { valid: false, message: "Contact number is required." };
  if (!n.country) return { valid: false, message: "Country is required." };
  if (!n.state) return { valid: false, message: "State is required." };
  if (!n.city) return { valid: false, message: "City is required." };
  return { valid: true };
}

export function isCustomerDraftComplete(draft: CustomerDraft): boolean {
  return validateCustomerDraft(draft).valid;
}

function buildApiPayload(draft: CustomerDraft) {
  const n = normalizeCustomerDraft(draft);
  return {
    name: n.name,
    father_name: n.fatherName || null,
    cnic: n.cnic || null,
    ntn: n.ntn || null,
    contact_person: n.contactPerson || null,
    phone: n.phone || null,
    email: n.email || null,
    type: n.type,
    entity_kind: n.entityKind,
    country: n.country,
    state: n.state,
    city: n.city || null,
    postal_code: n.postalCode || null,
    address_line_1: n.addressLine1 || null,
    address_line_2: n.addressLine2 || null,
  };
}

function load(): CustomerRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Array<Partial<CustomerRecord>>;
    if (!Array.isArray(parsed)) return [];
    return dedupeById(
      parsed.map((item) => {
        if (!item?.id) return null;
        const balance = Math.abs(Number(item.balance ?? 0));
        return {
          ...item,
          id: String(item.id),
          balance: Number.isFinite(balance) ? Math.round(balance * 100) / 100 : 0,
          balanceSide: item.balanceSide === "Cr" ? "Cr" : "Dr",
          totalPurchases: Math.round(Number(item.totalPurchases ?? 0)) || 0,
          totalSpent: Number(item.totalSpent ?? 0) || 0,
          currency: (item.currency ?? "PKR").toString().trim() || "PKR",
        } as CustomerRecord;
      }).filter((r): r is CustomerRecord => r != null),
    );
  } catch {
    return [];
  }
}

function save(items: CustomerRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listCustomers(): CustomerRecord[] {
  return load().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function getCustomerById(id: ID): CustomerRecord | undefined {
  return load().find((c) => c.id === id);
}

export async function syncCustomersFromApi() {
  try {
    const response = await api.get("/api/v1/customers", {
      params: { page: 1, per_page: 200 },
    });
    const rows = extractRowsFromResponse(response.data);
    const records = rows.map(mapApiRecord).filter((r) => Boolean(r.id));
    save(records);
    return listCustomers();
  } catch (error) {
    if (!(isAxiosError(error) && error.response?.status === 403)) {
      console.error("[Customers] syncCustomersFromApi failed", error);
    }
    throw error;
  }
}

export async function createCustomer(draft: CustomerDraft): Promise<UpsertCustomerResult> {
  const items = load();
  const payload = buildApiPayload(draft);
  const response = await api.post<UpsertCustomerApiResponse>("/api/v1/customers", payload);
  const created = response.data?.data;
  if (!created || created.id == null) {
    throw new Error("Invalid create customer response.");
  }
  const record = mapApiRecord(created);
  const idx = items.findIndex((c) => c.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Customer created successfully." };
}

export async function updateCustomer(id: ID, draft: CustomerDraft): Promise<UpsertCustomerResult | null> {
  const items = load();
  const idx = items.findIndex((c) => c.id === id);
  if (idx === -1) return null;
  const payload = buildApiPayload(draft);
  const response = await api.put<UpsertCustomerApiResponse>(`/api/v1/customers/${id}`, payload);
  const updated = response.data?.data;
  if (!updated) {
    throw new Error("Invalid update customer response.");
  }
  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);
  return { record, message: response.data?.message || "Customer updated successfully." };
}

export async function deleteCustomer(id: ID): Promise<DeleteCustomerResult> {
  const response = await api.delete<DeleteCustomerApiResponse>(`/api/v1/customers/${id}`);
  const items = load();
  const next = items.filter((c) => c.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return { success: removed, message: response.data?.message || "Customer deleted successfully." };
}

export function recordToCustomerDraft(c: CustomerRecord): CustomerDraft {
  return {
    name: c.name,
    fatherName: c.fatherName ?? "",
    cnic: c.cnic ?? "",
    ntn: c.ntn ?? "",
    contactPerson: c.contactPerson ?? "",
    phone: c.phone ?? "",
    email: c.email ?? "",
    type: normalizePartyType(c.type, "customer") as CustomerPartyType,
    entityKind: normalizePartyEntityKind(c.entityKind),
    country: c.country ?? null,
    state: c.state ?? null,
    city: c.city ?? "",
    postalCode: c.postalCode ?? "",
    addressLine1: c.addressLine1 ?? "",
    addressLine2: c.addressLine2 ?? "",
  };
}
